<template>
  <div class="min-h-[900px]">
    <main class="pb-24">
      <section
        class="flex flex-col items-center pt-16 pb-12 lg:pt-[102px] lg:pb-16"
      >
        <div
          class="mx-auto min-w-[320px] max-w-[1010px] px-4 max-[767px]:w-full max-[767px]:px-4"
        >
          <h1 class="landing_h1 mb-6 text-center">
            <Translate translate-key="page_data.tradeline-brokerage.h1" />
          </h1>
          <ServiceContentSection
            :text-content="t('tradeline_brokerage_page.data.1.content')"
            img="/services/become-a-tradeline-broker.png"
            :alt="t('tradeline_brokerage_page.alt')"
            :width="350"
            :height="250"
            :button-text="t('tradeline_brokerage_page.data.1.btn_text')"
            button-to="/signup?type=broker"
            button-variant="landing1"
            :button-text-two="t('tradeline_brokerage_page.data.1.title')"
            button-link-two="/blog/ultimate-guide-on-authorized-user-tradelines#section7"
            button-variant-two="landingOutline"
            button-two-target="_blank"
            button-size="xl"
            buttons-below
            image-centered
            reversed
          />
        </div>
      </section>

      <LazyLandingWhatWeOffer :show-broker-cta="true" />

      <section class="mx-auto max-w-6xl px-1 md:px-4 py-8">
        <h2 class="landing_h2 mb-8 text-center max-[767px]:text-center">
          <Translate translate-key="tradeline_brokerage_page.title" />
        </h2>
        <ServiceContentSection
          :title="t('tradeline_brokerage_page.data.2.title')"
          :text-content="t('tradeline_brokerage_page.data.2.content')"
          img="/landing/purchase-tradelines-fast-and-easily.png"
          :alt="t('tradeline_brokerage_page.alt_2')"
          :width="350"
          :height="270"
          title-above
          title-above-class="landing_2"
          reversed
          list-style="check"
        />
        <ServiceContentSection
          :title="t('tradeline_brokerage_page.data.3.title')"
          :text-content="t('tradeline_brokerage_page.data.3.content')"
          img="/landing/reliable-customer-support-for-your-tradelines.png"
          :alt="t('tradeline_brokerage_page.alt_3')"
          :width="340"
          :height="285"
          title-above
          title-above-class="landing_h2"
          list-style="check"
        />
        <ServiceContentSection
          :title="t('tradeline_brokerage_page.data.4.title')"
          :text-content="t('tradeline_brokerage_page.data.4.content')"
          img="/landing/proven-tradeline-industry-reputation.png"
          :alt="t('tradeline_brokerage_page.alt_4')"
          :width="340"
          :height="215"
          title-above
          title-above-class="landing_h2"
          :reversed="true"
          list-style="check"
        />
      </section>

      <LazyLandingLegality />

      <LazyLandingPageTestimonials
        v-if="testimonialsData?.length"
        :testimonials-data="testimonialsData"
        :max-items="3"
        show-waves
      />

      <LazyStartBrokeringSection />
    </main>
  </div>
</template>
<script setup lang="ts">
import Translate from "~/shared/components/i18n/Translate/Translate.vue";
import ServiceContentSection from "~/features/landing-page/components/ServiceContentSection.vue";
import type { TestimonialItem } from "~/features/landing-page/composables/useLandingTestimonials";
import { useI18n } from "vue-i18n";
import { useAuthFetch } from "~/shared/composables/useAuthFetch";

definePageMeta({ layout: "public" });

const { t } = useI18n();
const localePath = useLocalePath();
const config = useRuntimeConfig();
const { authFetch } = useAuthFetch();

const { data: testimonialsRaw } = await useAsyncData(
  "tradeline-brokerage-testimonials",
  () =>
    authFetch<TestimonialItem[] | { results: TestimonialItem[] }>(
      `${config.public.baseURL}v1/testimonials/`,
      { params: { type: 1, id_in: "7,24,25", limit: 3, offset: 0 } },
    ).catch(() => []),
);
const testimonialsData = computed(() => {
  const d = testimonialsRaw.value;
  return (Array.isArray(d) ? d : d?.results) ?? [];
});

const siteUrl = (config.public.siteUrl as string) || "";
const route = useRoute();

useSeoMeta({
  title: () => t("page_data.tradeline-brokerage.seo_title"),
  description: () => t("page_data.tradeline-brokerage.seo_description"),
  ogTitle: () => t("page_data.tradeline-brokerage.seo_title"),
  ogDescription: () => t("page_data.tradeline-brokerage.seo_description"),
  ogUrl: () => (siteUrl ? `${siteUrl}${route.path}` : undefined),
  ogImage: () => (siteUrl ? `${siteUrl}/services/afp-fb.jpeg` : undefined),
  ogImageAlt: () => t("tradeline_brokerage_page.og_image_alt"),
  titleTemplate: () => "%s | Wholesale Tradelines",
});
useHead(() => ({
  meta: [{ name: "keywords", content: t("page_data.tradeline-brokerage.seo_keywords") }],
}));
</script>
